import * as React from 'react';
import React__default, { useCallback, memo, useRef, useEffect } from 'react';
import { usePlateEditorRef, getPluginOptions, useEventPlateId, usePlateEditorState } from '@udecode/plate-core';
import { getEmojiOnSelectItem, KEY_EMOJI, useEmojiPicker, FrequentEmojiStorage, EmojiFloatingLibrary, EmojiFloatingIndexSearch, EmojiSettings } from '@udecode/plate-emoji';
import { Combobox } from '@udecode/plate-ui-combobox';
import { createStyles } from '@udecode/plate-styled-components';
import _styled, { css } from 'styled-components';
import { useDropdownControls, ToolbarButton } from '@udecode/plate-ui-toolbar';

const emojiCategoryIcons = {
  activity: {
    outline: /*#__PURE__*/React__default.createElement("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 24 24"
    }, /*#__PURE__*/React__default.createElement("path", {
      d: "M12 0C5.373 0 0 5.372 0 12c0 6.627 5.373 12 12 12 6.628 0 12-5.373 12-12 0-6.628-5.372-12-12-12m9.949 11H17.05c.224-2.527 1.232-4.773 1.968-6.113A9.966 9.966 0 0 1 21.949 11M13 11V2.051a9.945 9.945 0 0 1 4.432 1.564c-.858 1.491-2.156 4.22-2.392 7.385H13zm-2 0H8.961c-.238-3.165-1.536-5.894-2.393-7.385A9.95 9.95 0 0 1 11 2.051V11zm0 2v8.949a9.937 9.937 0 0 1-4.432-1.564c.857-1.492 2.155-4.221 2.393-7.385H11zm4.04 0c.236 3.164 1.534 5.893 2.392 7.385A9.92 9.92 0 0 1 13 21.949V13h2.04zM4.982 4.887C5.718 6.227 6.726 8.473 6.951 11h-4.9a9.977 9.977 0 0 1 2.931-6.113M2.051 13h4.9c-.226 2.527-1.233 4.771-1.969 6.113A9.972 9.972 0 0 1 2.051 13m16.967 6.113c-.735-1.342-1.744-3.586-1.968-6.113h4.899a9.961 9.961 0 0 1-2.931 6.113"
    })),
    solid: /*#__PURE__*/React__default.createElement("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 512 512"
    }, /*#__PURE__*/React__default.createElement("path", {
      d: "M16.17 337.5c0 44.98 7.565 83.54 13.98 107.9C35.22 464.3 50.46 496 174.9 496c9.566 0 19.59-.4707 29.84-1.271L17.33 307.3C16.53 317.6 16.17 327.7 16.17 337.5zM495.8 174.5c0-44.98-7.565-83.53-13.98-107.9c-4.688-17.54-18.34-31.23-36.04-35.95C435.5 27.91 392.9 16 337 16c-9.564 0-19.59 .4707-29.84 1.271l187.5 187.5C495.5 194.4 495.8 184.3 495.8 174.5zM26.77 248.8l236.3 236.3c142-36.1 203.9-150.4 222.2-221.1L248.9 26.87C106.9 62.96 45.07 177.2 26.77 248.8zM256 335.1c0 9.141-7.474 16-16 16c-4.094 0-8.188-1.564-11.31-4.689L164.7 283.3C161.6 280.2 160 276.1 160 271.1c0-8.529 6.865-16 16-16c4.095 0 8.189 1.562 11.31 4.688l64.01 64C254.4 327.8 256 331.9 256 335.1zM304 287.1c0 9.141-7.474 16-16 16c-4.094 0-8.188-1.564-11.31-4.689L212.7 235.3C209.6 232.2 208 228.1 208 223.1c0-9.141 7.473-16 16-16c4.094 0 8.188 1.562 11.31 4.688l64.01 64.01C302.5 279.8 304 283.9 304 287.1zM256 175.1c0-9.141 7.473-16 16-16c4.094 0 8.188 1.562 11.31 4.688l64.01 64.01c3.125 3.125 4.688 7.219 4.688 11.31c0 9.133-7.468 16-16 16c-4.094 0-8.189-1.562-11.31-4.688l-64.01-64.01C257.6 184.2 256 180.1 256 175.1z"
    }))
  },
  custom: {
    outline: /*#__PURE__*/React__default.createElement("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 448 512"
    }, /*#__PURE__*/React__default.createElement("path", {
      d: "M417.1 368c-5.937 10.27-16.69 16-27.75 16c-5.422 0-10.92-1.375-15.97-4.281L256 311.4V448c0 17.67-14.33 32-31.1 32S192 465.7 192 448V311.4l-118.3 68.29C68.67 382.6 63.17 384 57.75 384c-11.06 0-21.81-5.734-27.75-16c-8.828-15.31-3.594-34.88 11.72-43.72L159.1 256L41.72 187.7C26.41 178.9 21.17 159.3 29.1 144C36.63 132.5 49.26 126.7 61.65 128.2C65.78 128.7 69.88 130.1 73.72 132.3L192 200.6V64c0-17.67 14.33-32 32-32S256 46.33 256 64v136.6l118.3-68.29c3.838-2.213 7.939-3.539 12.07-4.051C398.7 126.7 411.4 132.5 417.1 144c8.828 15.31 3.594 34.88-11.72 43.72L288 256l118.3 68.28C421.6 333.1 426.8 352.7 417.1 368z"
    })),
    solid: /*#__PURE__*/React__default.createElement("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 448 512"
    }, /*#__PURE__*/React__default.createElement("path", {
      d: "M417.1 368c-5.937 10.27-16.69 16-27.75 16c-5.422 0-10.92-1.375-15.97-4.281L256 311.4V448c0 17.67-14.33 32-31.1 32S192 465.7 192 448V311.4l-118.3 68.29C68.67 382.6 63.17 384 57.75 384c-11.06 0-21.81-5.734-27.75-16c-8.828-15.31-3.594-34.88 11.72-43.72L159.1 256L41.72 187.7C26.41 178.9 21.17 159.3 29.1 144C36.63 132.5 49.26 126.7 61.65 128.2C65.78 128.7 69.88 130.1 73.72 132.3L192 200.6V64c0-17.67 14.33-32 32-32S256 46.33 256 64v136.6l118.3-68.29c3.838-2.213 7.939-3.539 12.07-4.051C398.7 126.7 411.4 132.5 417.1 144c8.828 15.31 3.594 34.88-11.72 43.72L288 256l118.3 68.28C421.6 333.1 426.8 352.7 417.1 368z"
    }))
  },
  flags: {
    outline: /*#__PURE__*/React__default.createElement("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 24 24"
    }, /*#__PURE__*/React__default.createElement("path", {
      d: "M0 0l6.084 24H8L1.916 0zM21 5h-4l-1-4H4l3 12h3l1 4h13L21 5zM6.563 3h7.875l2 8H8.563l-2-8zm8.832 10l-2.856 1.904L12.063 13h3.332zM19 13l-1.5-6h1.938l2 8H16l3-2z"
    })),
    solid: /*#__PURE__*/React__default.createElement("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 512 512"
    }, /*#__PURE__*/React__default.createElement("path", {
      d: "M64 496C64 504.8 56.75 512 48 512h-32C7.25 512 0 504.8 0 496V32c0-17.75 14.25-32 32-32s32 14.25 32 32V496zM476.3 0c-6.365 0-13.01 1.35-19.34 4.233c-45.69 20.86-79.56 27.94-107.8 27.94c-59.96 0-94.81-31.86-163.9-31.87C160.9 .3055 131.6 4.867 96 15.75v350.5c32-9.984 59.87-14.1 84.85-14.1c73.63 0 124.9 31.78 198.6 31.78c31.91 0 68.02-5.971 111.1-23.09C504.1 355.9 512 344.4 512 332.1V30.73C512 11.1 495.3 0 476.3 0z"
    }))
  },
  foods: {
    outline: /*#__PURE__*/React__default.createElement("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 24 24"
    }, /*#__PURE__*/React__default.createElement("path", {
      d: "M17 4.978c-1.838 0-2.876.396-3.68.934.513-1.172 1.768-2.934 4.68-2.934a1 1 0 0 0 0-2c-2.921 0-4.629 1.365-5.547 2.512-.064.078-.119.162-.18.244C11.73 1.838 10.798.023 9.207.023 8.579.022 7.85.306 7 .978 5.027 2.54 5.329 3.902 6.492 4.999 3.609 5.222 0 7.352 0 12.969c0 4.582 4.961 11.009 9 11.009 1.975 0 2.371-.486 3-1 .629.514 1.025 1 3 1 4.039 0 9-6.418 9-11 0-5.953-4.055-8-7-8M8.242 2.546c.641-.508.943-.523.965-.523.426.169.975 1.405 1.357 3.055-1.527-.629-2.741-1.352-2.98-1.846.059-.112.241-.356.658-.686M15 21.978c-1.08 0-1.21-.109-1.559-.402l-.176-.146c-.367-.302-.816-.452-1.266-.452s-.898.15-1.266.452l-.176.146c-.347.292-.477.402-1.557.402-2.813 0-7-5.389-7-9.009 0-5.823 4.488-5.991 5-5.991 1.939 0 2.484.471 3.387 1.251l.323.276a1.995 1.995 0 0 0 2.58 0l.323-.276c.902-.78 1.447-1.251 3.387-1.251.512 0 5 .168 5 6 0 3.617-4.187 9-7 9"
    })),
    solid: /*#__PURE__*/React__default.createElement("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 512 512"
    }, /*#__PURE__*/React__default.createElement("path", {
      d: "M481.9 270.1C490.9 279.1 496 291.3 496 304C496 316.7 490.9 328.9 481.9 337.9C472.9 346.9 460.7 352 448 352H64C51.27 352 39.06 346.9 30.06 337.9C21.06 328.9 16 316.7 16 304C16 291.3 21.06 279.1 30.06 270.1C39.06 261.1 51.27 256 64 256H448C460.7 256 472.9 261.1 481.9 270.1zM475.3 388.7C478.3 391.7 480 395.8 480 400V416C480 432.1 473.3 449.3 461.3 461.3C449.3 473.3 432.1 480 416 480H96C79.03 480 62.75 473.3 50.75 461.3C38.74 449.3 32 432.1 32 416V400C32 395.8 33.69 391.7 36.69 388.7C39.69 385.7 43.76 384 48 384H464C468.2 384 472.3 385.7 475.3 388.7zM50.39 220.8C45.93 218.6 42.03 215.5 38.97 211.6C35.91 207.7 33.79 203.2 32.75 198.4C31.71 193.5 31.8 188.5 32.99 183.7C54.98 97.02 146.5 32 256 32C365.5 32 457 97.02 479 183.7C480.2 188.5 480.3 193.5 479.2 198.4C478.2 203.2 476.1 207.7 473 211.6C469.1 215.5 466.1 218.6 461.6 220.8C457.2 222.9 452.3 224 447.3 224H64.67C59.73 224 54.84 222.9 50.39 220.8zM372.7 116.7C369.7 119.7 368 123.8 368 128C368 131.2 368.9 134.3 370.7 136.9C372.5 139.5 374.1 141.6 377.9 142.8C380.8 143.1 384 144.3 387.1 143.7C390.2 143.1 393.1 141.6 395.3 139.3C397.6 137.1 399.1 134.2 399.7 131.1C400.3 128 399.1 124.8 398.8 121.9C397.6 118.1 395.5 116.5 392.9 114.7C390.3 112.9 387.2 111.1 384 111.1C379.8 111.1 375.7 113.7 372.7 116.7V116.7zM244.7 84.69C241.7 87.69 240 91.76 240 96C240 99.16 240.9 102.3 242.7 104.9C244.5 107.5 246.1 109.6 249.9 110.8C252.8 111.1 256 112.3 259.1 111.7C262.2 111.1 265.1 109.6 267.3 107.3C269.6 105.1 271.1 102.2 271.7 99.12C272.3 96.02 271.1 92.8 270.8 89.88C269.6 86.95 267.5 84.45 264.9 82.7C262.3 80.94 259.2 79.1 256 79.1C251.8 79.1 247.7 81.69 244.7 84.69V84.69zM116.7 116.7C113.7 119.7 112 123.8 112 128C112 131.2 112.9 134.3 114.7 136.9C116.5 139.5 118.1 141.6 121.9 142.8C124.8 143.1 128 144.3 131.1 143.7C134.2 143.1 137.1 141.6 139.3 139.3C141.6 137.1 143.1 134.2 143.7 131.1C144.3 128 143.1 124.8 142.8 121.9C141.6 118.1 139.5 116.5 136.9 114.7C134.3 112.9 131.2 111.1 128 111.1C123.8 111.1 119.7 113.7 116.7 116.7L116.7 116.7z"
    }))
  },
  frequent: {
    outline: /*#__PURE__*/React__default.createElement("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 24 24"
    }, /*#__PURE__*/React__default.createElement("path", {
      d: "M13 4h-2l-.001 7H9v2h2v2h2v-2h4v-2h-4z"
    }), /*#__PURE__*/React__default.createElement("path", {
      d: "M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0m0 22C6.486 22 2 17.514 2 12S6.486 2 12 2s10 4.486 10 10-4.486 10-10 10"
    })),
    solid: /*#__PURE__*/React__default.createElement("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 512 512"
    }, /*#__PURE__*/React__default.createElement("path", {
      d: "M256 512C114.6 512 0 397.4 0 256C0 114.6 114.6 0 256 0C397.4 0 512 114.6 512 256C512 397.4 397.4 512 256 512zM232 256C232 264 236 271.5 242.7 275.1L338.7 339.1C349.7 347.3 364.6 344.3 371.1 333.3C379.3 322.3 376.3 307.4 365.3 300L280 243.2V120C280 106.7 269.3 96 255.1 96C242.7 96 231.1 106.7 231.1 120L232 256z"
    }))
  },
  nature: {
    outline: /*#__PURE__*/React__default.createElement("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 24 24"
    }, /*#__PURE__*/React__default.createElement("path", {
      d: "M15.5 8a1.5 1.5 0 1 0 .001 3.001A1.5 1.5 0 0 0 15.5 8M8.5 8a1.5 1.5 0 1 0 .001 3.001A1.5 1.5 0 0 0 8.5 8"
    }), /*#__PURE__*/React__default.createElement("path", {
      d: "M18.933 0h-.027c-.97 0-2.138.787-3.018 1.497-1.274-.374-2.612-.51-3.887-.51-1.285 0-2.616.133-3.874.517C7.245.79 6.069 0 5.093 0h-.027C3.352 0 .07 2.67.002 7.026c-.039 2.479.276 4.238 1.04 5.013.254.258.882.677 1.295.882.191 3.177.922 5.238 2.536 6.38.897.637 2.187.949 3.2 1.102C8.04 20.6 8 20.795 8 21c0 1.773 2.35 3 4 3 1.648 0 4-1.227 4-3 0-.201-.038-.393-.072-.586 2.573-.385 5.435-1.877 5.925-7.587.396-.22.887-.568 1.104-.788.763-.774 1.079-2.534 1.04-5.013C23.929 2.67 20.646 0 18.933 0M3.223 9.135c-.237.281-.837 1.155-.884 1.238-.15-.41-.368-1.349-.337-3.291.051-3.281 2.478-4.972 3.091-5.031.256.015.731.27 1.265.646-1.11 1.171-2.275 2.915-2.352 5.125-.133.546-.398.858-.783 1.313M12 22c-.901 0-1.954-.693-2-1 0-.654.475-1.236 1-1.602V20a1 1 0 1 0 2 0v-.602c.524.365 1 .947 1 1.602-.046.307-1.099 1-2 1m3-3.48v.02a4.752 4.752 0 0 0-1.262-1.02c1.092-.516 2.239-1.334 2.239-2.217 0-1.842-1.781-2.195-3.977-2.195-2.196 0-3.978.354-3.978 2.195 0 .883 1.148 1.701 2.238 2.217A4.8 4.8 0 0 0 9 18.539v-.025c-1-.076-2.182-.281-2.973-.842-1.301-.92-1.838-3.045-1.853-6.478l.023-.041c.496-.826 1.49-1.45 1.804-3.102 0-2.047 1.357-3.631 2.362-4.522C9.37 3.178 10.555 3 11.948 3c1.447 0 2.685.192 3.733.57 1 .9 2.316 2.465 2.316 4.48.313 1.651 1.307 2.275 1.803 3.102.035.058.068.117.102.178-.059 5.967-1.949 7.01-4.902 7.19m6.628-8.202c-.037-.065-.074-.13-.113-.195a7.587 7.587 0 0 0-.739-.987c-.385-.455-.648-.768-.782-1.313-.076-2.209-1.241-3.954-2.353-5.124.531-.376 1.004-.63 1.261-.647.636.071 3.044 1.764 3.096 5.031.027 1.81-.347 3.218-.37 3.235"
    })),
    solid: /*#__PURE__*/React__default.createElement("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 576 512"
    }, /*#__PURE__*/React__default.createElement("path", {
      d: "M332.7 19.85C334.6 8.395 344.5 0 356.1 0C363.6 0 370.6 3.52 375.1 9.502L392 32H444.1C456.8 32 469.1 37.06 478.1 46.06L496 64H552C565.3 64 576 74.75 576 88V112C576 156.2 540.2 192 496 192H426.7L421.6 222.5L309.6 158.5L332.7 19.85zM448 64C439.2 64 432 71.16 432 80C432 88.84 439.2 96 448 96C456.8 96 464 88.84 464 80C464 71.16 456.8 64 448 64zM416 256.1V480C416 497.7 401.7 512 384 512H352C334.3 512 320 497.7 320 480V364.8C295.1 377.1 268.8 384 240 384C211.2 384 184 377.1 160 364.8V480C160 497.7 145.7 512 128 512H96C78.33 512 64 497.7 64 480V249.8C35.23 238.9 12.64 214.5 4.836 183.3L.9558 167.8C-3.331 150.6 7.094 133.2 24.24 128.1C41.38 124.7 58.76 135.1 63.05 152.2L66.93 167.8C70.49 182 83.29 191.1 97.97 191.1H303.8L416 256.1z"
    }))
  },
  objects: {
    outline: /*#__PURE__*/React__default.createElement("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 24 24"
    }, /*#__PURE__*/React__default.createElement("path", {
      d: "M12 0a9 9 0 0 0-5 16.482V21s2.035 3 5 3 5-3 5-3v-4.518A9 9 0 0 0 12 0zm0 2c3.86 0 7 3.141 7 7s-3.14 7-7 7-7-3.141-7-7 3.14-7 7-7zM9 17.477c.94.332 1.946.523 3 .523s2.06-.19 3-.523v.834c-.91.436-1.925.689-3 .689a6.924 6.924 0 0 1-3-.69v-.833zm.236 3.07A8.854 8.854 0 0 0 12 21c.965 0 1.888-.167 2.758-.451C14.155 21.173 13.153 22 12 22c-1.102 0-2.117-.789-2.764-1.453z"
    }), /*#__PURE__*/React__default.createElement("path", {
      d: "M14.745 12.449h-.004c-.852-.024-1.188-.858-1.577-1.824-.421-1.061-.703-1.561-1.182-1.566h-.009c-.481 0-.783.497-1.235 1.537-.436.982-.801 1.811-1.636 1.791l-.276-.043c-.565-.171-.853-.691-1.284-1.794-.125-.313-.202-.632-.27-.913-.051-.213-.127-.53-.195-.634C7.067 9.004 7.039 9 6.99 9A1 1 0 0 1 7 7h.01c1.662.017 2.015 1.373 2.198 2.134.486-.981 1.304-2.058 2.797-2.075 1.531.018 2.28 1.153 2.731 2.141l.002-.008C14.944 8.424 15.327 7 16.979 7h.032A1 1 0 1 1 17 9h-.011c-.149.076-.256.474-.319.709a6.484 6.484 0 0 1-.311.951c-.429.973-.79 1.789-1.614 1.789"
    })),
    solid: /*#__PURE__*/React__default.createElement("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 384 512"
    }, /*#__PURE__*/React__default.createElement("path", {
      d: "M112.1 454.3c0 6.297 1.816 12.44 5.284 17.69l17.14 25.69c5.25 7.875 17.17 14.28 26.64 14.28h61.67c9.438 0 21.36-6.401 26.61-14.28l17.08-25.68c2.938-4.438 5.348-12.37 5.348-17.7L272 415.1h-160L112.1 454.3zM191.4 .0132C89.44 .3257 16 82.97 16 175.1c0 44.38 16.44 84.84 43.56 115.8c16.53 18.84 42.34 58.23 52.22 91.45c.0313 .25 .0938 .5166 .125 .7823h160.2c.0313-.2656 .0938-.5166 .125-.7823c9.875-33.22 35.69-72.61 52.22-91.45C351.6 260.8 368 220.4 368 175.1C368 78.61 288.9-.2837 191.4 .0132zM192 96.01c-44.13 0-80 35.89-80 79.1C112 184.8 104.8 192 96 192S80 184.8 80 176c0-61.76 50.25-111.1 112-111.1c8.844 0 16 7.159 16 16S200.8 96.01 192 96.01z"
    }))
  },
  people: {
    outline: /*#__PURE__*/React__default.createElement("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 24 24"
    }, /*#__PURE__*/React__default.createElement("path", {
      d: "M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0m0 22C6.486 22 2 17.514 2 12S6.486 2 12 2s10 4.486 10 10-4.486 10-10 10"
    }), /*#__PURE__*/React__default.createElement("path", {
      d: "M8 7a2 2 0 1 0-.001 3.999A2 2 0 0 0 8 7M16 7a2 2 0 1 0-.001 3.999A2 2 0 0 0 16 7M15.232 15c-.693 1.195-1.87 2-3.349 2-1.477 0-2.655-.805-3.347-2H15m3-2H6a6 6 0 1 0 12 0"
    })),
    solid: /*#__PURE__*/React__default.createElement("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 512 512"
    }, /*#__PURE__*/React__default.createElement("path", {
      d: "M0 256C0 114.6 114.6 0 256 0C397.4 0 512 114.6 512 256C512 397.4 397.4 512 256 512C114.6 512 0 397.4 0 256zM256 432C332.1 432 396.2 382 415.2 314.1C419.1 300.4 407.8 288 393.6 288H118.4C104.2 288 92.92 300.4 96.76 314.1C115.8 382 179.9 432 256 432V432zM176.4 160C158.7 160 144.4 174.3 144.4 192C144.4 209.7 158.7 224 176.4 224C194 224 208.4 209.7 208.4 192C208.4 174.3 194 160 176.4 160zM336.4 224C354 224 368.4 209.7 368.4 192C368.4 174.3 354 160 336.4 160C318.7 160 304.4 174.3 304.4 192C304.4 209.7 318.7 224 336.4 224z"
    }))
  },
  places: {
    outline: /*#__PURE__*/React__default.createElement("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 24 24"
    }, /*#__PURE__*/React__default.createElement("path", {
      d: "M6.5 12C5.122 12 4 13.121 4 14.5S5.122 17 6.5 17 9 15.879 9 14.5 7.878 12 6.5 12m0 3c-.275 0-.5-.225-.5-.5s.225-.5.5-.5.5.225.5.5-.225.5-.5.5M17.5 12c-1.378 0-2.5 1.121-2.5 2.5s1.122 2.5 2.5 2.5 2.5-1.121 2.5-2.5-1.122-2.5-2.5-2.5m0 3c-.275 0-.5-.225-.5-.5s.225-.5.5-.5.5.225.5.5-.225.5-.5.5"
    }), /*#__PURE__*/React__default.createElement("path", {
      d: "M22.482 9.494l-1.039-.346L21.4 9h.6c.552 0 1-.439 1-.992 0-.006-.003-.008-.003-.008H23c0-1-.889-2-1.984-2h-.642l-.731-1.717C19.262 3.012 18.091 2 16.764 2H7.236C5.909 2 4.738 3.012 4.357 4.283L3.626 6h-.642C1.889 6 1 7 1 8h.003S1 8.002 1 8.008C1 8.561 1.448 9 2 9h.6l-.043.148-1.039.346a2.001 2.001 0 0 0-1.359 2.097l.751 7.508a1 1 0 0 0 .994.901H3v1c0 1.103.896 2 2 2h2c1.104 0 2-.897 2-2v-1h6v1c0 1.103.896 2 2 2h2c1.104 0 2-.897 2-2v-1h1.096a.999.999 0 0 0 .994-.901l.751-7.508a2.001 2.001 0 0 0-1.359-2.097M6.273 4.857C6.402 4.43 6.788 4 7.236 4h9.527c.448 0 .834.43.963.857L19.313 9H4.688l1.585-4.143zM7 21H5v-1h2v1zm12 0h-2v-1h2v1zm2.189-3H2.811l-.662-6.607L3 11h18l.852.393L21.189 18z"
    })),
    solid: /*#__PURE__*/React__default.createElement("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 512 512"
    }, /*#__PURE__*/React__default.createElement("path", {
      d: "M39.61 196.8L74.8 96.29C88.27 57.78 124.6 32 165.4 32H346.6C387.4 32 423.7 57.78 437.2 96.29L472.4 196.8C495.6 206.4 512 229.3 512 256V448C512 465.7 497.7 480 480 480H448C430.3 480 416 465.7 416 448V400H96V448C96 465.7 81.67 480 64 480H32C14.33 480 0 465.7 0 448V256C0 229.3 16.36 206.4 39.61 196.8V196.8zM109.1 192H402.9L376.8 117.4C372.3 104.6 360.2 96 346.6 96H165.4C151.8 96 139.7 104.6 135.2 117.4L109.1 192zM96 256C78.33 256 64 270.3 64 288C64 305.7 78.33 320 96 320C113.7 320 128 305.7 128 288C128 270.3 113.7 256 96 256zM416 320C433.7 320 448 305.7 448 288C448 270.3 433.7 256 416 256C398.3 256 384 270.3 384 288C384 305.7 398.3 320 416 320z"
    }))
  },
  symbols: {
    outline: /*#__PURE__*/React__default.createElement("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 24 24"
    }, /*#__PURE__*/React__default.createElement("path", {
      d: "M0 0h11v2H0zM4 11h3V6h4V4H0v2h4zM15.5 17c1.381 0 2.5-1.116 2.5-2.493s-1.119-2.493-2.5-2.493S13 13.13 13 14.507 14.119 17 15.5 17m0-2.986c.276 0 .5.222.5.493 0 .272-.224.493-.5.493s-.5-.221-.5-.493.224-.493.5-.493M21.5 19.014c-1.381 0-2.5 1.116-2.5 2.493S20.119 24 21.5 24s2.5-1.116 2.5-2.493-1.119-2.493-2.5-2.493m0 2.986a.497.497 0 0 1-.5-.493c0-.271.224-.493.5-.493s.5.222.5.493a.497.497 0 0 1-.5.493M22 13l-9 9 1.513 1.5 8.99-9.009zM17 11c2.209 0 4-1.119 4-2.5V2s.985-.161 1.498.949C23.01 4.055 23 6 23 6s1-1.119 1-3.135C24-.02 21 0 21 0h-2v6.347A5.853 5.853 0 0 0 17 6c-2.209 0-4 1.119-4 2.5s1.791 2.5 4 2.5M10.297 20.482l-1.475-1.585a47.54 47.54 0 0 1-1.442 1.129c-.307-.288-.989-1.016-2.045-2.183.902-.836 1.479-1.466 1.729-1.892s.376-.871.376-1.336c0-.592-.273-1.178-.818-1.759-.546-.581-1.329-.871-2.349-.871-1.008 0-1.79.293-2.344.879-.556.587-.832 1.181-.832 1.784 0 .813.419 1.748 1.256 2.805-.847.614-1.444 1.208-1.794 1.784a3.465 3.465 0 0 0-.523 1.833c0 .857.308 1.56.924 2.107.616.549 1.423.823 2.42.823 1.173 0 2.444-.379 3.813-1.137L8.235 24h2.819l-2.09-2.383 1.333-1.135zm-6.736-6.389a1.02 1.02 0 0 1 .73-.286c.31 0 .559.085.747.254a.849.849 0 0 1 .283.659c0 .518-.419 1.112-1.257 1.784-.536-.651-.805-1.231-.805-1.742a.901.901 0 0 1 .302-.669M3.74 22c-.427 0-.778-.116-1.057-.349-.279-.232-.418-.487-.418-.766 0-.594.509-1.288 1.527-2.083.968 1.134 1.717 1.946 2.248 2.438-.921.507-1.686.76-2.3.76"
    })),
    solid: /*#__PURE__*/React__default.createElement("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 512 512"
    }, /*#__PURE__*/React__default.createElement("path", {
      d: "M500.3 7.251C507.7 13.33 512 22.41 512 31.1V175.1C512 202.5 483.3 223.1 447.1 223.1C412.7 223.1 383.1 202.5 383.1 175.1C383.1 149.5 412.7 127.1 447.1 127.1V71.03L351.1 90.23V207.1C351.1 234.5 323.3 255.1 287.1 255.1C252.7 255.1 223.1 234.5 223.1 207.1C223.1 181.5 252.7 159.1 287.1 159.1V63.1C287.1 48.74 298.8 35.61 313.7 32.62L473.7 .6198C483.1-1.261 492.9 1.173 500.3 7.251H500.3zM74.66 303.1L86.5 286.2C92.43 277.3 102.4 271.1 113.1 271.1H174.9C185.6 271.1 195.6 277.3 201.5 286.2L213.3 303.1H239.1C266.5 303.1 287.1 325.5 287.1 351.1V463.1C287.1 490.5 266.5 511.1 239.1 511.1H47.1C21.49 511.1-.0019 490.5-.0019 463.1V351.1C-.0019 325.5 21.49 303.1 47.1 303.1H74.66zM143.1 359.1C117.5 359.1 95.1 381.5 95.1 407.1C95.1 434.5 117.5 455.1 143.1 455.1C170.5 455.1 191.1 434.5 191.1 407.1C191.1 381.5 170.5 359.1 143.1 359.1zM440.3 367.1H496C502.7 367.1 508.6 372.1 510.1 378.4C513.3 384.6 511.6 391.7 506.5 396L378.5 508C372.9 512.1 364.6 513.3 358.6 508.9C352.6 504.6 350.3 496.6 353.3 489.7L391.7 399.1H336C329.3 399.1 323.4 395.9 321 389.6C318.7 383.4 320.4 376.3 325.5 371.1L453.5 259.1C459.1 255 467.4 254.7 473.4 259.1C479.4 263.4 481.6 271.4 478.7 278.3L440.3 367.1zM116.7 219.1L19.85 119.2C-8.112 90.26-6.614 42.31 24.85 15.34C51.82-8.137 93.26-3.642 118.2 21.83L128.2 32.32L137.7 21.83C162.7-3.642 203.6-8.137 231.6 15.34C262.6 42.31 264.1 90.26 236.1 119.2L139.7 219.1C133.2 225.6 122.7 225.6 116.7 219.1H116.7z"
    }))
  }
};
const emojiSearchIcons = {
  loupe: /*#__PURE__*/React__default.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 20 20"
  }, /*#__PURE__*/React__default.createElement("path", {
    d: "M12.9 14.32a8 8 0 1 1 1.41-1.41l5.35 5.33-1.42 1.42-5.33-5.34zM8 14A6 6 0 1 0 8 2a6 6 0 0 0 0 12z"
  })),
  delete: /*#__PURE__*/React__default.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 20 20"
  }, /*#__PURE__*/React__default.createElement("path", {
    d: "M10 8.586L2.929 1.515 1.515 2.929 8.586 10l-7.071 7.071 1.414 1.414L10 11.414l7.071 7.071 1.414-1.414L11.414 10l7.071-7.071-1.414-1.414L10 8.586z"
  }))
};

function _extends$1() {
  _extends$1 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };
  return _extends$1.apply(this, arguments);
}

const EmojiComboboxItem = ({
  item
}) => {
  const {
    data: {
      id,
      emoji
    }
  } = item;
  return /*#__PURE__*/React__default.createElement("div", null, emoji, " :", id, ":");
};

const useEmojiCombobox = pluginKey => {
  const editor = usePlateEditorRef();
  const {
    trigger
  } = getPluginOptions(editor, pluginKey);
  const onSelectItem = getEmojiOnSelectItem({
    key: pluginKey
  });
  return {
    trigger: trigger,
    onSelectItem
  };
};
const EmojiCombobox = ({
  pluginKey = KEY_EMOJI,
  id = pluginKey,
  ...props
}) => {
  const {
    trigger,
    onSelectItem
  } = useEmojiCombobox(pluginKey);
  return /*#__PURE__*/React__default.createElement(Combobox, _extends$1({
    id: id,
    trigger: trigger,
    controlled: true,
    onSelectItem: onSelectItem,
    onRenderItem: EmojiComboboxItem
  }, props));
};

const getEmojiPickerStyles = props => createStyles({
  prefixClassNames: 'EmojiPicker',
  ...props
}, {
  root: [css(["box-shadow:rgb(15 15 15 / 5%) 0 0 0 1px,rgb(15 15 15 / 10%) 0 3px 6px,rgb(15 15 15 / 20%) 0 9px 24px;"]), {
    "width": "316px",
    "height": "350px",
    "display": "flex",
    "flexDirection": "column",
    "--tw-bg-opacity": "1",
    "backgroundColor": "rgba(255, 255, 255, var(--tw-bg-opacity))",
    "borderRadius": "0.25rem"
  }]
});

const getEmojiPickerContentStyles = props => createStyles({
  prefixClassNames: 'EmojiPickerContent',
  ...props
}, {
  root: [{
    "paddingLeft": "0.75rem",
    "paddingRight": "0.75rem",
    "overflowX": "hidden",
    "overflowY": "auto",
    "height": "100%",
    "minHeight": "50%"
  }, css(["::-webkit-scrollbar{width:16px;}::-webkit-scrollbar-button{width:0;height:0;display:none;}:hover::-webkit-scrollbar-thumb{background-color:#f3f4f6;}::-webkit-scrollbar-thumb{min-height:65px;border:4px solid #fff;border-radius:8px;}::-webkit-scrollbar-track{border:0;}"])],
  content: [{
    "height": "100%"
  }],
  sticky: [{
    "position": "sticky",
    "zIndex": "1",
    "--tw-backdrop-blur": "blur(4px)",
    "backdropFilter": "var(--tw-backdrop-filter)",
    "backgroundColor": "rgba(255, 255, 255, 0.9)",
    "top": "-1px",
    "paddingTop": "0.25rem",
    "paddingBottom": "0.25rem",
    "paddingLeft": "0.25rem",
    "paddingRight": "0.25rem"
  }],
  category: [{
    "position": "relative",
    "display": "flex",
    "flexWrap": "wrap"
  }],
  button: [{
    "position": "relative",
    "cursor": "pointer",
    "display": "flex",
    "justifyContent": "center",
    "alignItems": "center",
    "width": "36px",
    "height": "36px",
    "fontSize": "1.5rem",
    "lineHeight": "1",
    "borderStyle": "none",
    "backgroundColor": "rgba(0, 0, 0, 0)"
  }],
  buttonBg: [{
    "borderRadius": "9999px",
    "opacity": "0",
    "position": "absolute",
    ".group:hover &": {
      "opacity": "1"
    }
  }, css(["inset:0;background-color:rgba(0,0,0,0.05);"])]
});

const Button = /*#__PURE__*/memo(({
  index,
  emoji,
  onSelect,
  onMouseOver,
  ...props
}) => {
  const {
    button,
    buttonBg
  } = getEmojiPickerContentStyles({ ...props
  });
  return /*#__PURE__*/React__default.createElement(_StyledButton$2, {
    type: "button",
    "aria-label": emoji.skins[0].native,
    tabIndex: -1,
    "data-index": index,
    onClick: () => onSelect(emoji),
    onMouseEnter: () => onMouseOver(emoji),
    onMouseLeave: () => onMouseOver(undefined),
    className: "group",
    $_css: button === null || button === void 0 ? void 0 : button.css
  }, /*#__PURE__*/React__default.createElement(_StyledDiv$5, {
    "aria-hidden": "true",
    $_css2: buttonBg.css
  }), /*#__PURE__*/React__default.createElement(_StyledSpan$1, {
    "data-emoji-set": "native"
  }, emoji.skins[0].native));
});
const RowOfButtons = /*#__PURE__*/memo(({
  row,
  emojiLibrary,
  onSelectEmoji,
  onMouseOver
}) => /*#__PURE__*/React__default.createElement(_StyledDiv2$3, {
  key: row.id,
  "data-index": row.id
}, row.elements.map((emojiId, index) => /*#__PURE__*/React__default.createElement(Button, {
  key: emojiId,
  index: index,
  emoji: emojiLibrary.getEmoji(emojiId),
  onSelect: onSelectEmoji,
  onMouseOver: onMouseOver
}))));
const EmojiPickerContent = ({
  i18n,
  onSelectEmoji,
  onMouseOver,
  emojiLibrary,
  isSearching = false,
  searchResult,
  visibleCategories,
  refs,
  settings,
  ...props
}) => {
  var _styles$content;

  const styles = getEmojiPickerContentStyles({ ...props
  });
  const getRowWidth = settings.perLine.value * settings.buttonSize.value;
  const isCategoryVisible = useCallback(categoryId => {
    return visibleCategories.has(categoryId) ? visibleCategories.get(categoryId) : false;
  }, [visibleCategories]);
  const EmojiList = useCallback(() => {
    return emojiLibrary.getGrid().sections().map(({
      id: categoryId
    }) => {
      var _styles$sticky, _styles$category;

      const section = emojiLibrary.getGrid().section(categoryId);
      const {
        buttonSize
      } = settings;
      return /*#__PURE__*/React__default.createElement("div", {
        key: categoryId,
        "data-id": categoryId,
        ref: section.root,
        style: {
          width: getRowWidth
        }
      }, /*#__PURE__*/React__default.createElement(_StyledDiv3$1, {
        $_css3: (_styles$sticky = styles.sticky) === null || _styles$sticky === void 0 ? void 0 : _styles$sticky.css
      }, i18n.categories[categoryId]), /*#__PURE__*/React__default.createElement(_StyledDiv4$1, {
        style: {
          height: section.getRows().length * buttonSize.value
        },
        $_css4: (_styles$category = styles.category) === null || _styles$category === void 0 ? void 0 : _styles$category.css
      }, isCategoryVisible(categoryId) && section.getRows().map((row, index) => /*#__PURE__*/React__default.createElement(RowOfButtons, {
        key: index,
        emojiLibrary: emojiLibrary,
        row: row,
        onSelectEmoji: onSelectEmoji,
        onMouseOver: onMouseOver
      }))));
    });
  }, [emojiLibrary, getRowWidth, i18n.categories, isCategoryVisible, onSelectEmoji, onMouseOver, settings, styles]);
  const SearchList = useCallback(() => {
    var _styles$sticky2, _styles$category2;

    return /*#__PURE__*/React__default.createElement("div", {
      "data-id": "search",
      style: {
        width: getRowWidth
      }
    }, /*#__PURE__*/React__default.createElement(_StyledDiv5$1, {
      $_css5: (_styles$sticky2 = styles.sticky) === null || _styles$sticky2 === void 0 ? void 0 : _styles$sticky2.css
    }, i18n.searchResult), /*#__PURE__*/React__default.createElement(_StyledDiv6$1, {
      $_css6: (_styles$category2 = styles.category) === null || _styles$category2 === void 0 ? void 0 : _styles$category2.css
    }, searchResult.map((emoji, index) => /*#__PURE__*/React__default.createElement(Button, {
      key: emoji.id,
      index: index,
      emoji: emojiLibrary.getEmoji(emoji.id),
      onSelect: onSelectEmoji,
      onMouseOver: onMouseOver
    }))));
  }, [emojiLibrary, getRowWidth, i18n.searchResult, searchResult, onSelectEmoji, onMouseOver, styles]);
  return /*#__PURE__*/React__default.createElement(_StyledDiv7$1, {
    "data-id": "scroll",
    ref: refs.current.contentRoot,
    $_css7: styles.root.css
  }, /*#__PURE__*/React__default.createElement(_StyledDiv8$1, {
    ref: refs.current.content,
    $_css8: (_styles$content = styles.content) === null || _styles$content === void 0 ? void 0 : _styles$content.css
  }, isSearching ? SearchList() : EmojiList()));
};

var _StyledButton$2 = _styled("button").withConfig({
  displayName: "EmojiPickerContent___StyledButton",
  componentId: "sc-1650az3-0"
})(["", ""], p => p.$_css);

var _StyledDiv$5 = _styled("div").withConfig({
  displayName: "EmojiPickerContent___StyledDiv",
  componentId: "sc-1650az3-1"
})(["", ""], p => p.$_css2);

var _StyledSpan$1 = _styled("span").withConfig({
  displayName: "EmojiPickerContent___StyledSpan",
  componentId: "sc-1650az3-2"
})({
  position: 'relative'
});

var _StyledDiv2$3 = _styled("div").withConfig({
  displayName: "EmojiPickerContent___StyledDiv2",
  componentId: "sc-1650az3-3"
})({
  display: 'flex',
  'flex-direction': 'row'
});

var _StyledDiv3$1 = _styled("div").withConfig({
  displayName: "EmojiPickerContent___StyledDiv3",
  componentId: "sc-1650az3-4"
})(["", ""], p => p.$_css3);

var _StyledDiv4$1 = _styled("div").withConfig({
  displayName: "EmojiPickerContent___StyledDiv4",
  componentId: "sc-1650az3-5"
})(["", ""], p => p.$_css4);

var _StyledDiv5$1 = _styled("div").withConfig({
  displayName: "EmojiPickerContent___StyledDiv5",
  componentId: "sc-1650az3-6"
})(["", ""], p => p.$_css5);

var _StyledDiv6$1 = _styled("div").withConfig({
  displayName: "EmojiPickerContent___StyledDiv6",
  componentId: "sc-1650az3-7"
})(["", ""], p => p.$_css6);

var _StyledDiv7$1 = _styled("div").withConfig({
  displayName: "EmojiPickerContent___StyledDiv7",
  componentId: "sc-1650az3-8"
})(["", ""], p => p.$_css7);

var _StyledDiv8$1 = _styled("div").withConfig({
  displayName: "EmojiPickerContent___StyledDiv8",
  componentId: "sc-1650az3-9"
})(["", ""], p => p.$_css8);

const getEmojiPickerNavigationStyles = props => createStyles({
  prefixClassNames: 'EmojiPickerNavigation',
  ...props
}, {
  root: [{
    "padding": "0.75rem",
    "marginBottom": "0.625rem",
    "borderStyle": "solid",
    "borderWidth": "0px",
    "borderBottomWidth": "1px",
    "--tw-border-opacity": "1",
    "borderBottomColor": "rgba(243, 244, 246, var(--tw-border-opacity))"
  }],
  button: [{
    "display": "flex",
    "flexGrow": "1",
    "alignItems": "center",
    "justifyContent": "center",
    "borderStyle": "none",
    "backgroundColor": "rgba(0, 0, 0, 0)",
    "fontSize": "0.875rem",
    "lineHeight": "1.25rem",
    "cursor": "pointer",
    "--tw-text-opacity": "1",
    "color": "rgba(107, 114, 128, var(--tw-text-opacity))",
    "fill": "currentColor",
    ":hover": {
      "--tw-text-opacity": "1",
      "color": "rgba(31, 41, 55, var(--tw-text-opacity))"
    }
  }],
  selected: [{
    "--tw-text-opacity": "1",
    "color": "rgba(37, 99, 235, var(--tw-text-opacity))",
    "fill": "currentColor",
    "pointerEvents": "none"
  }],
  bar: [{
    "position": "absolute",
    "opacity": "1",
    "left": "0px",
    "bottom": "-0.75rem",
    "height": "3px",
    "width": "100%",
    "--tw-bg-opacity": "1",
    "backgroundColor": "rgba(37, 99, 235, var(--tw-bg-opacity))",
    "transitionProperty": "transform",
    "transitionTimingFunction": "cubic-bezier(0.4, 0, 0.2, 1)",
    "transitionDuration": "200ms",
    "borderTopLeftRadius": "0.25rem",
    "borderTopRightRadius": "0.25rem"
  }]
});

const getBarProperty = (emojiLibrary, focusedCategory) => {
  let width = 0;
  let position = 0;

  if (focusedCategory) {
    width = 100 / emojiLibrary.getGrid().size;
    position = focusedCategory ? emojiLibrary.indexOf(focusedCategory) * 100 : 0;
  }

  return {
    width,
    position
  };
};

const EmojiPickerNavigation = ({
  i18n,
  icons,
  emojiLibrary,
  focusedCategory,
  onClick,
  ...props
}) => {
  const {
    root,
    button,
    selected,
    bar
  } = getEmojiPickerNavigationStyles({ ...props
  });
  const {
    width,
    position
  } = getBarProperty(emojiLibrary, focusedCategory);

  const CategoryButton = ({
    categoryId
  }) => {
    return /*#__PURE__*/React__default.createElement(_StyledButton$1, {
      key: categoryId,
      "aria-label": i18n.categories[categoryId],
      title: i18n.categories[categoryId],
      type: "button",
      onClick: () => onClick(categoryId),
      $_css: button === null || button === void 0 ? void 0 : button.css,
      $_css2: categoryId === focusedCategory ? selected === null || selected === void 0 ? void 0 : selected.css : ''
    }, /*#__PURE__*/React__default.createElement("span", {
      style: {
        width: '20px',
        height: '20px'
      }
    }, icons.categories[categoryId].outline));
  };

  return /*#__PURE__*/React__default.createElement(_StyledNav, {
    id: "emoji-nav",
    $_css3: root.css
  }, /*#__PURE__*/React__default.createElement(_StyledDiv$4, null, emojiLibrary.getGrid().sections().map(({
    id
  }) => /*#__PURE__*/React__default.createElement(CategoryButton, {
    categoryId: id,
    key: id
  })), /*#__PURE__*/React__default.createElement(_StyledDiv2$2, {
    style: {
      visibility: `${focusedCategory ? 'visible' : 'hidden'}`,
      width: `${width}%`,
      transform: `translateX(${position}%)`
    },
    $_css4: bar === null || bar === void 0 ? void 0 : bar.css
  })));
};

var _StyledButton$1 = _styled("button").withConfig({
  displayName: "EmojiPickerNavigation___StyledButton",
  componentId: "sc-zmjd7t-0"
})(["", " ", ""], p => p.$_css, p => p.$_css2);

var _StyledNav = _styled("nav").withConfig({
  displayName: "EmojiPickerNavigation___StyledNav",
  componentId: "sc-zmjd7t-1"
})(["", ""], p => p.$_css3);

var _StyledDiv$4 = _styled("div").withConfig({
  displayName: "EmojiPickerNavigation___StyledDiv",
  componentId: "sc-zmjd7t-2"
})({
  "position": "relative",
  "display": "flex"
});

var _StyledDiv2$2 = _styled("div").withConfig({
  displayName: "EmojiPickerNavigation___StyledDiv2",
  componentId: "sc-zmjd7t-3"
})(["", ""], p => p.$_css4);

const getEmojiPickerPreviewStyles = props => {
  return createStyles({
    prefixClassNames: 'EmojiPickerPreview',
    ...props
  }, {
    root: [{
      "display": "flex",
      "padding": "0.5rem",
      "alignItems": "center",
      "borderStyle": "solid",
      "borderWidth": "0px",
      "borderTopWidth": "1px",
      "--tw-border-opacity": "1",
      "borderTopColor": "rgba(243, 244, 246, var(--tw-border-opacity))"
    }],
    content: [{
      "paddingLeft": "0.5rem",
      "overflow": "hidden"
    }],
    emoji: [{
      "display": "flex",
      "alignItems": "center",
      "justifyContent": "center",
      "fontSize": "1.875rem",
      "lineHeight": "2.25rem"
    }],
    title: [{
      "--tw-text-opacity": "1",
      "color": "rgba(75, 85, 99, var(--tw-text-opacity))",
      "fontSize": "0.875rem",
      "lineHeight": "1.25rem",
      "overflow": "hidden",
      "textOverflow": "ellipsis",
      "whiteSpace": "nowrap"
    }],
    subtitle: [{
      "--tw-text-opacity": "1",
      "color": "rgba(156, 163, 175, var(--tw-text-opacity))",
      "fontSize": "0.75rem",
      "lineHeight": "1rem",
      "overflow": "hidden",
      "textOverflow": "ellipsis",
      "whiteSpace": "nowrap"
    }],
    text: [{
      "--tw-text-opacity": "1",
      "color": "rgba(156, 163, 175, var(--tw-text-opacity))",
      "fontSize": "1.125rem",
      "lineHeight": "1.75rem",
      "overflow": "hidden",
      "textOverflow": "ellipsis",
      "whiteSpace": "nowrap"
    }]
  });
};

const EmojiPreview = ({
  emoji,
  ...props
}) => {
  var _styles$emoji, _styles$content, _styles$title, _styles$subtitle;

  const styles = getEmojiPickerPreviewStyles({ ...props
  });
  return /*#__PURE__*/React__default.createElement(_StyledDiv$3, {
    $_css: styles.root.css
  }, /*#__PURE__*/React__default.createElement(_StyledDiv2$1, {
    $_css2: (_styles$emoji = styles.emoji) === null || _styles$emoji === void 0 ? void 0 : _styles$emoji.css
  }, emoji === null || emoji === void 0 ? void 0 : emoji.skins[0].native), /*#__PURE__*/React__default.createElement(_StyledDiv3, {
    $_css3: (_styles$content = styles.content) === null || _styles$content === void 0 ? void 0 : _styles$content.css
  }, /*#__PURE__*/React__default.createElement(_StyledDiv4, {
    $_css4: (_styles$title = styles.title) === null || _styles$title === void 0 ? void 0 : _styles$title.css
  }, emoji === null || emoji === void 0 ? void 0 : emoji.name), /*#__PURE__*/React__default.createElement(_StyledDiv5, {
    $_css5: (_styles$subtitle = styles.subtitle) === null || _styles$subtitle === void 0 ? void 0 : _styles$subtitle.css
  }, `:${emoji === null || emoji === void 0 ? void 0 : emoji.id}:`)));
};

const NoEmoji = ({
  i18n,
  ...props
}) => {
  var _styles$emoji2, _styles$content2, _styles$title2, _styles$subtitle2;

  const styles = getEmojiPickerPreviewStyles({ ...props
  });
  return /*#__PURE__*/React__default.createElement(_StyledDiv6, {
    $_css6: styles.root.css
  }, /*#__PURE__*/React__default.createElement(_StyledDiv7, {
    $_css7: (_styles$emoji2 = styles.emoji) === null || _styles$emoji2 === void 0 ? void 0 : _styles$emoji2.css
  }, "\uD83D\uDE22"), /*#__PURE__*/React__default.createElement(_StyledDiv8, {
    $_css8: (_styles$content2 = styles.content) === null || _styles$content2 === void 0 ? void 0 : _styles$content2.css
  }, /*#__PURE__*/React__default.createElement(_StyledDiv9, {
    $_css9: (_styles$title2 = styles.title) === null || _styles$title2 === void 0 ? void 0 : _styles$title2.css
  }, i18n.searchNoResultsTitle), /*#__PURE__*/React__default.createElement(_StyledDiv10, {
    $_css10: (_styles$subtitle2 = styles.subtitle) === null || _styles$subtitle2 === void 0 ? void 0 : _styles$subtitle2.css
  }, i18n.searchNoResultsSubtitle)));
};

const PickAnEmoji = ({
  i18n,
  ...props
}) => {
  var _styles$emoji3, _styles$content3, _styles$text;

  const styles = getEmojiPickerPreviewStyles({ ...props
  });
  return /*#__PURE__*/React__default.createElement(_StyledDiv11, {
    $_css11: styles.root.css
  }, /*#__PURE__*/React__default.createElement(_StyledDiv12, {
    $_css12: (_styles$emoji3 = styles.emoji) === null || _styles$emoji3 === void 0 ? void 0 : _styles$emoji3.css
  }, "\u261D\uFE0F"), /*#__PURE__*/React__default.createElement(_StyledDiv13, {
    $_css13: (_styles$content3 = styles.content) === null || _styles$content3 === void 0 ? void 0 : _styles$content3.css
  }, /*#__PURE__*/React__default.createElement(_StyledDiv14, {
    $_css14: (_styles$text = styles.text) === null || _styles$text === void 0 ? void 0 : _styles$text.css
  }, i18n.pick)));
};

const EmojiPickerPreview = ({
  emoji,
  hasFound = true,
  isSearching = false,
  i18n,
  ...props
}) => {
  const showPickEmoji = !emoji && !(isSearching && !hasFound);
  const showNoEmoji = isSearching && !hasFound;
  const showPreview = emoji;
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, showPreview && /*#__PURE__*/React__default.createElement(EmojiPreview, _extends$1({
    emoji: emoji
  }, props)), showPickEmoji && /*#__PURE__*/React__default.createElement(PickAnEmoji, _extends$1({
    i18n: i18n
  }, props)), showNoEmoji && /*#__PURE__*/React__default.createElement(NoEmoji, _extends$1({
    i18n: i18n
  }, props)));
};

var _StyledDiv$3 = _styled("div").withConfig({
  displayName: "EmojiPickerPreview___StyledDiv",
  componentId: "sc-1q2bsjv-0"
})(["", ""], p => p.$_css);

var _StyledDiv2$1 = _styled("div").withConfig({
  displayName: "EmojiPickerPreview___StyledDiv2",
  componentId: "sc-1q2bsjv-1"
})(["", ""], p => p.$_css2);

var _StyledDiv3 = _styled("div").withConfig({
  displayName: "EmojiPickerPreview___StyledDiv3",
  componentId: "sc-1q2bsjv-2"
})(["", ""], p => p.$_css3);

var _StyledDiv4 = _styled("div").withConfig({
  displayName: "EmojiPickerPreview___StyledDiv4",
  componentId: "sc-1q2bsjv-3"
})(["", ""], p => p.$_css4);

var _StyledDiv5 = _styled("div").withConfig({
  displayName: "EmojiPickerPreview___StyledDiv5",
  componentId: "sc-1q2bsjv-4"
})(["", ""], p => p.$_css5);

var _StyledDiv6 = _styled("div").withConfig({
  displayName: "EmojiPickerPreview___StyledDiv6",
  componentId: "sc-1q2bsjv-5"
})(["", ""], p => p.$_css6);

var _StyledDiv7 = _styled("div").withConfig({
  displayName: "EmojiPickerPreview___StyledDiv7",
  componentId: "sc-1q2bsjv-6"
})(["", ""], p => p.$_css7);

var _StyledDiv8 = _styled("div").withConfig({
  displayName: "EmojiPickerPreview___StyledDiv8",
  componentId: "sc-1q2bsjv-7"
})(["", ""], p => p.$_css8);

var _StyledDiv9 = _styled("div").withConfig({
  displayName: "EmojiPickerPreview___StyledDiv9",
  componentId: "sc-1q2bsjv-8"
})(["", ""], p => p.$_css9);

var _StyledDiv10 = _styled("div").withConfig({
  displayName: "EmojiPickerPreview___StyledDiv10",
  componentId: "sc-1q2bsjv-9"
})(["", ""], p => p.$_css10);

var _StyledDiv11 = _styled("div").withConfig({
  displayName: "EmojiPickerPreview___StyledDiv11",
  componentId: "sc-1q2bsjv-10"
})(["", ""], p => p.$_css11);

var _StyledDiv12 = _styled("div").withConfig({
  displayName: "EmojiPickerPreview___StyledDiv12",
  componentId: "sc-1q2bsjv-11"
})(["", ""], p => p.$_css12);

var _StyledDiv13 = _styled("div").withConfig({
  displayName: "EmojiPickerPreview___StyledDiv13",
  componentId: "sc-1q2bsjv-12"
})(["", ""], p => p.$_css13);

var _StyledDiv14 = _styled("div").withConfig({
  displayName: "EmojiPickerPreview___StyledDiv14",
  componentId: "sc-1q2bsjv-13"
})(["", ""], p => p.$_css14);

const getEmojiPickerSearchBarStyles = props => createStyles({
  prefixClassNames: 'EmojiPickerSearchBar',
  ...props
}, {
  root: [{
    "display": "flex",
    "paddingLeft": "0.5rem",
    "paddingRight": "0.5rem",
    "alignItems": "center"
  }],
  input: [{
    "width": "100%",
    "borderWidth": "0px",
    "borderRadius": "0.5rem",
    "display": "block",
    "paddingLeft": "2rem",
    "paddingRight": "2rem",
    "paddingTop": "0.5rem",
    "paddingBottom": "0.5rem",
    "outline": "2px solid transparent",
    "outlineOffset": "2px",
    "--tw-bg-opacity": "1",
    "backgroundColor": "rgba(243, 244, 246, var(--tw-bg-opacity))",
    "appearance": "none"
  }]
});

const EmojiPickerSearchBar = ({
  i18n,
  searchValue,
  setSearch,
  children,
  ...props
}) => {
  var _styles$input;

  const styles = getEmojiPickerSearchBarStyles({ ...props
  });
  return /*#__PURE__*/React__default.createElement(_StyledDiv$2, {
    $_css: styles.root.css
  }, /*#__PURE__*/React__default.createElement(_StyledDiv2, null, /*#__PURE__*/React__default.createElement(_StyledInput, {
    type: "text",
    placeholder: i18n.search,
    autoComplete: "off",
    "aria-label": "Search",
    onChange: event => setSearch(event.target.value),
    value: searchValue,
    $_css2: (_styles$input = styles.input) === null || _styles$input === void 0 ? void 0 : _styles$input.css
  }), children));
};

var _StyledDiv$2 = _styled("div").withConfig({
  displayName: "EmojiPickerSearchBar___StyledDiv",
  componentId: "sc-1lluf68-0"
})(["", ""], p => p.$_css);

var _StyledDiv2 = _styled("div").withConfig({
  displayName: "EmojiPickerSearchBar___StyledDiv2",
  componentId: "sc-1lluf68-1"
})({
  "position": "relative",
  "display": "flex",
  "flexGrow": "1"
});

var _StyledInput = _styled("input").withConfig({
  displayName: "EmojiPickerSearchBar___StyledInput",
  componentId: "sc-1lluf68-2"
})(["", ""], p => p.$_css2);

const getEmojiPickerSearchAndClearStyles = props => createStyles({
  prefixClassNames: 'EmojiPickerSearchAndClear',
  ...props
}, {
  loupeIcon: [{
    "display": "flex",
    "position": "absolute",
    "width": "1.25rem",
    "height": "1.25rem",
    "zIndex": "10",
    "top": "50%",
    "left": "0.5rem"
  }, css(["transform:translateY(-50%);"])],
  clearIcon: [{
    "borderStyle": "none",
    "backgroundColor": "rgba(0, 0, 0, 0)",
    "display": "flex",
    "position": "absolute",
    "width": "2rem",
    "height": "2rem",
    "top": "50%",
    "right": "0px",
    "cursor": "pointer"
  }, css(["transform:translateY(-50%);"])]
});

function unwrapExports (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

function createCommonjsModule(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

var _extends_1 = createCommonjsModule(function (module) {
function _extends() {
  module.exports = _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  }, module.exports.__esModule = true, module.exports["default"] = module.exports;
  return _extends.apply(this, arguments);
}

module.exports = _extends, module.exports.__esModule = true, module.exports["default"] = module.exports;
});

var _extends = unwrapExports(_extends_1);

var defineProperty = createCommonjsModule(function (module) {
function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

module.exports = _defineProperty, module.exports.__esModule = true, module.exports["default"] = module.exports;
});

var _defineProperty = unwrapExports(defineProperty);

var objectWithoutPropertiesLoose = createCommonjsModule(function (module) {
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

module.exports = _objectWithoutPropertiesLoose, module.exports.__esModule = true, module.exports["default"] = module.exports;
});

unwrapExports(objectWithoutPropertiesLoose);

var objectWithoutProperties = createCommonjsModule(function (module) {
function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = objectWithoutPropertiesLoose(source, excluded);
  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

module.exports = _objectWithoutProperties, module.exports.__esModule = true, module.exports["default"] = module.exports;
});

var _objectWithoutProperties = unwrapExports(objectWithoutProperties);

function memoize(fn) {
  var cache = {};
  return function (arg) {
    if (cache[arg] === undefined) cache[arg] = fn(arg);
    return cache[arg];
  };
}

var reactPropsRegex = /^((children|dangerouslySetInnerHTML|key|ref|autoFocus|defaultValue|defaultChecked|innerHTML|suppressContentEditableWarning|suppressHydrationWarning|valueLink|accept|acceptCharset|accessKey|action|allow|allowUserMedia|allowPaymentRequest|allowFullScreen|allowTransparency|alt|async|autoComplete|autoPlay|capture|cellPadding|cellSpacing|challenge|charSet|checked|cite|classID|className|cols|colSpan|content|contentEditable|contextMenu|controls|controlsList|coords|crossOrigin|data|dateTime|decoding|default|defer|dir|disabled|disablePictureInPicture|download|draggable|encType|form|formAction|formEncType|formMethod|formNoValidate|formTarget|frameBorder|headers|height|hidden|high|href|hrefLang|htmlFor|httpEquiv|id|inputMode|integrity|is|keyParams|keyType|kind|label|lang|list|loading|loop|low|marginHeight|marginWidth|max|maxLength|media|mediaGroup|method|min|minLength|multiple|muted|name|nonce|noValidate|open|optimum|pattern|placeholder|playsInline|poster|preload|profile|radioGroup|readOnly|referrerPolicy|rel|required|reversed|role|rows|rowSpan|sandbox|scope|scoped|scrolling|seamless|selected|shape|size|sizes|slot|span|spellCheck|src|srcDoc|srcLang|srcSet|start|step|style|summary|tabIndex|target|title|type|useMap|value|width|wmode|wrap|about|datatype|inlist|prefix|property|resource|typeof|vocab|autoCapitalize|autoCorrect|autoSave|color|inert|itemProp|itemScope|itemType|itemID|itemRef|on|results|security|unselectable|accentHeight|accumulate|additive|alignmentBaseline|allowReorder|alphabetic|amplitude|arabicForm|ascent|attributeName|attributeType|autoReverse|azimuth|baseFrequency|baselineShift|baseProfile|bbox|begin|bias|by|calcMode|capHeight|clip|clipPathUnits|clipPath|clipRule|colorInterpolation|colorInterpolationFilters|colorProfile|colorRendering|contentScriptType|contentStyleType|cursor|cx|cy|d|decelerate|descent|diffuseConstant|direction|display|divisor|dominantBaseline|dur|dx|dy|edgeMode|elevation|enableBackground|end|exponent|externalResourcesRequired|fill|fillOpacity|fillRule|filter|filterRes|filterUnits|floodColor|floodOpacity|focusable|fontFamily|fontSize|fontSizeAdjust|fontStretch|fontStyle|fontVariant|fontWeight|format|from|fr|fx|fy|g1|g2|glyphName|glyphOrientationHorizontal|glyphOrientationVertical|glyphRef|gradientTransform|gradientUnits|hanging|horizAdvX|horizOriginX|ideographic|imageRendering|in|in2|intercept|k|k1|k2|k3|k4|kernelMatrix|kernelUnitLength|kerning|keyPoints|keySplines|keyTimes|lengthAdjust|letterSpacing|lightingColor|limitingConeAngle|local|markerEnd|markerMid|markerStart|markerHeight|markerUnits|markerWidth|mask|maskContentUnits|maskUnits|mathematical|mode|numOctaves|offset|opacity|operator|order|orient|orientation|origin|overflow|overlinePosition|overlineThickness|panose1|paintOrder|pathLength|patternContentUnits|patternTransform|patternUnits|pointerEvents|points|pointsAtX|pointsAtY|pointsAtZ|preserveAlpha|preserveAspectRatio|primitiveUnits|r|radius|refX|refY|renderingIntent|repeatCount|repeatDur|requiredExtensions|requiredFeatures|restart|result|rotate|rx|ry|scale|seed|shapeRendering|slope|spacing|specularConstant|specularExponent|speed|spreadMethod|startOffset|stdDeviation|stemh|stemv|stitchTiles|stopColor|stopOpacity|strikethroughPosition|strikethroughThickness|string|stroke|strokeDasharray|strokeDashoffset|strokeLinecap|strokeLinejoin|strokeMiterlimit|strokeOpacity|strokeWidth|surfaceScale|systemLanguage|tableValues|targetX|targetY|textAnchor|textDecoration|textRendering|textLength|to|transform|u1|u2|underlinePosition|underlineThickness|unicode|unicodeBidi|unicodeRange|unitsPerEm|vAlphabetic|vHanging|vIdeographic|vMathematical|values|vectorEffect|version|vertAdvY|vertOriginX|vertOriginY|viewBox|viewTarget|visibility|widths|wordSpacing|writingMode|x|xHeight|x1|x2|xChannelSelector|xlinkActuate|xlinkArcrole|xlinkHref|xlinkRole|xlinkShow|xlinkTitle|xlinkType|xmlBase|xmlns|xmlnsXlink|xmlLang|xmlSpace|y|y1|y2|yChannelSelector|z|zoomAndPan|for|class|autofocus)|(([Dd][Aa][Tt][Aa]|[Aa][Rr][Ii][Aa]|x)-.*))$/; // https://esbench.com/bench/5bfee68a4cd7e6009ef61d23

var index = memoize(function (prop) {
  return reactPropsRegex.test(prop) || prop.charCodeAt(0) === 111
  /* o */
  && prop.charCodeAt(1) === 110
  /* n */
  && prop.charCodeAt(2) < 91;
}
/* Z+1 */
);

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function isValidProp(key) {
  return index(key);
}

function filterSVGProps(props) {
  return Object.keys(props).reduce(function (p, k) {
    if (isValidProp(k)) {
      p[k] = props[k];
    }

    return p;
  }, {});
}

var StyledIconBaseBase = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var children = props.children,
      iconAttrs = props.iconAttrs;
      props.iconVerticalAlign;
      var iconViewBox = props.iconViewBox,
      size = props.size,
      title = props.title,
      otherProps = _objectWithoutProperties(props, ["children", "iconAttrs", "iconVerticalAlign", "iconViewBox", "size", "title"]);

  var iconProps = _objectSpread({
    viewBox: iconViewBox,
    height: props.height !== undefined ? props.height : size,
    width: props.width !== undefined ? props.width : size,
    'aria-hidden': title == null ? 'true' : undefined,
    focusable: 'false',
    role: title != null ? 'img' : undefined
  }, iconAttrs);

  var svgProps = filterSVGProps(otherProps);
  return /*#__PURE__*/React.createElement("svg", _extends({}, iconProps, svgProps, {
    ref: ref
  }), title && /*#__PURE__*/React.createElement("title", {
    key: "icon-title"
  }, title), children);
});
var StyledIconBase = /*#__PURE__*/_styled(StyledIconBaseBase).withConfig({
  displayName: "StyledIconBase",
  componentId: "ea9ulj-0"
})(["display:inline-block;vertical-align:", ";overflow:hidden;"], function (props) {
  return props.iconVerticalAlign;
});

var Clear = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var attrs = {
    "fill": "currentColor",
    "xmlns": "http://www.w3.org/2000/svg"
  };
  return /*#__PURE__*/React.createElement(StyledIconBase, _extends({
    iconAttrs: attrs,
    iconVerticalAlign: "middle",
    iconViewBox: "0 0 24 24"
  }, props, {
    ref: ref
  }), /*#__PURE__*/React.createElement("path", {
    fill: "none",
    d: "M0 0h24v24H0z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
  }));
});
Clear.displayName = 'Clear';

var Search = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var attrs = {
    "fill": "currentColor",
    "xmlns": "http://www.w3.org/2000/svg"
  };
  return /*#__PURE__*/React.createElement(StyledIconBase, _extends({
    iconAttrs: attrs,
    iconVerticalAlign: "middle",
    iconViewBox: "0 0 24 24"
  }, props, {
    ref: ref
  }), /*#__PURE__*/React.createElement("path", {
    fill: "none",
    d: "M0 0h24v24H0z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
  }));
});
Search.displayName = 'Search';

const EmojiPickerSearchAndClear = ({
  i18n,
  searchValue,
  clearSearch,
  ...props
}) => {
  var _styles$loupeIcon, _styles$clearIcon;

  const styles = getEmojiPickerSearchAndClearStyles({ ...props
  });
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(_StyledSpan, {
    $_css: (_styles$loupeIcon = styles.loupeIcon) === null || _styles$loupeIcon === void 0 ? void 0 : _styles$loupeIcon.css
  }, /*#__PURE__*/React__default.createElement(Search, null)), searchValue && /*#__PURE__*/React__default.createElement(_StyledButton, {
    title: i18n.clear,
    "aria-label": "Clear",
    type: "button",
    onClick: clearSearch,
    $_css2: (_styles$clearIcon = styles.clearIcon) === null || _styles$clearIcon === void 0 ? void 0 : _styles$clearIcon.css
  }, /*#__PURE__*/React__default.createElement(_StyledClear, null)));
};

var _StyledSpan = _styled("span").withConfig({
  displayName: "EmojiPickerSearchAndClear___StyledSpan",
  componentId: "sc-su8kpj-0"
})(["", ""], p => p.$_css);

var _StyledButton = _styled("button").withConfig({
  displayName: "EmojiPickerSearchAndClear___StyledButton",
  componentId: "sc-su8kpj-1"
})(["", ""], p => p.$_css2);

var _StyledClear = _styled(Clear).withConfig({
  displayName: "EmojiPickerSearchAndClear___StyledClear",
  componentId: "sc-su8kpj-2"
})({
  width: '100%',
  height: ' 100%'
});

const EmojiPicker = ({
  i18n,
  searchValue,
  setSearch,
  clearSearch,
  isSearching,
  hasFound,
  searchResult,
  emoji,
  onSelectEmoji,
  onMouseOver,
  emojiLibrary,
  icons,
  handleCategoryClick,
  focusedCategory,
  visibleCategories,
  refs,
  settings,
  ...props
}) => {
  const {
    root
  } = getEmojiPickerStyles(props);
  return /*#__PURE__*/React__default.createElement(_StyledDiv$1, {
    $_css: root.css
  }, /*#__PURE__*/React__default.createElement(EmojiPickerNavigation, {
    i18n: i18n,
    emojiLibrary: emojiLibrary,
    icons: icons,
    focusedCategory: focusedCategory,
    onClick: handleCategoryClick
  }), /*#__PURE__*/React__default.createElement(EmojiPickerSearchBar, {
    i18n: i18n,
    setSearch: setSearch,
    searchValue: searchValue
  }, /*#__PURE__*/React__default.createElement(EmojiPickerSearchAndClear, {
    i18n: i18n,
    clearSearch: clearSearch,
    searchValue: searchValue
  })), /*#__PURE__*/React__default.createElement(EmojiPickerContent, {
    i18n: i18n,
    emojiLibrary: emojiLibrary,
    isSearching: isSearching,
    searchResult: searchResult,
    visibleCategories: visibleCategories,
    settings: settings,
    onSelectEmoji: onSelectEmoji,
    onMouseOver: onMouseOver,
    refs: refs
  }), /*#__PURE__*/React__default.createElement(EmojiPickerPreview, {
    i18n: i18n,
    emoji: emoji,
    hasFound: hasFound,
    isSearching: isSearching
  }));
};

var _StyledDiv$1 = _styled("div").withConfig({
  displayName: "EmojiPicker___StyledDiv",
  componentId: "sc-179dk40-0"
})(["", ""], p => p.$_css);

const EmojiToolbarDropdownRoot = ({
  control,
  children,
  open,
  onOpen,
  onClose
}) => {
  const {
    styles,
    reference,
    floating
  } = useDropdownControls({
    open,
    onClose
  });
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("div", {
    ref: reference,
    onMouseDown: onOpen
  }, control), open && /*#__PURE__*/React__default.createElement(_StyledDiv, {
    ref: floating,
    style: styles
  }, children));
};

var _StyledDiv = _styled("div").withConfig({
  displayName: "EmojiToolbarDropdownRoot___StyledDiv",
  componentId: "sc-s95yjs-0"
})({
  "zIndex": "20 !important"
});

const EmojiToolbarDropdown = ({
  id,
  icon,
  EmojiPickerComponent = EmojiPicker,
  pluginKey,
  settings = EmojiSettings,
  closeOnSelect = true,
  ...rest
}) => {
  id = useEventPlateId(id);
  const editor = usePlateEditorState(id);
  const emojiFloatingLibraryRef = useRef();
  const emojiFloatingIndexSearchRef = useRef();
  const {
    onToggle,
    isOpen,
    ...emojiPickerState
  } = useEmojiPicker({
    closeOnSelect,
    editor,
    emojiLibrary: emojiFloatingLibraryRef.current,
    indexSearch: emojiFloatingIndexSearchRef.current
  });
  useEffect(() => {
    const frequentEmojiStorage = new FrequentEmojiStorage({
      limit: settings.showFrequent.limit
    });
    emojiFloatingLibraryRef.current = EmojiFloatingLibrary.getInstance(settings, frequentEmojiStorage);
    emojiFloatingIndexSearchRef.current = EmojiFloatingIndexSearch.getInstance(emojiFloatingLibraryRef.current);
  }, [settings]);
  return /*#__PURE__*/React__default.createElement(EmojiToolbarDropdownRoot, {
    control: /*#__PURE__*/React__default.createElement(ToolbarButton, _extends$1({
      active: isOpen,
      icon: icon
    }, rest)),
    open: isOpen,
    onOpen: onToggle,
    onClose: onToggle
  }, /*#__PURE__*/React__default.createElement(EmojiPickerComponent, _extends$1({}, emojiPickerState, {
    isOpen: isOpen,
    onToggle: onToggle,
    icons: {
      categories: emojiCategoryIcons,
      search: emojiSearchIcons
    },
    settings: settings
  })));
};

export { EmojiCombobox, EmojiComboboxItem, EmojiPicker, EmojiPickerContent, EmojiPickerNavigation, EmojiPickerPreview, EmojiPickerSearchAndClear, EmojiPickerSearchBar, EmojiToolbarDropdown, EmojiToolbarDropdownRoot, emojiCategoryIcons, emojiSearchIcons, getEmojiPickerContentStyles, getEmojiPickerNavigationStyles, getEmojiPickerPreviewStyles, getEmojiPickerSearchAndClearStyles, getEmojiPickerSearchBarStyles, getEmojiPickerStyles, useEmojiCombobox };
//# sourceMappingURL=index.es.js.map
